import type {
  EnderecoAr,
  ModalidadeEntregaAr,
  MotivoRetornoAr,
  OrigemEnderecoAr,
  SituacaoCobrancaAr,
  SituacaoEnderecoAr,
  SugestaoEnderecoAr,
} from '@/types/arbitral/envioArTypes';
import type { MotivoLiberacaoSentenca } from '@/shared/constants/motivoLiberacaoSentenca';

import type { PrazoAndamentoSelecao } from './detalheExtras';
import type { VinculosEscritaInput } from './partesEscrita';
import type {
  MotivoEncerramentoFisicoCitacao,
  SituacaoDaCitacao,
  StatusCitacaoRequerido,
} from './statusCitacao';

export type ProcessoArbitralStatus =
  | 'PROCESSO_DISTRIBUIDO'
  | 'TRIAGEM_EM_ANDAMENTO'
  | 'TRIAGEM_CONCLUIDA'
  | 'EM_CITACAO'
  | 'EM_ANDAMENTO'
  | 'SENTENCA_EMITIDA'
  | 'CONCLUIDO'
  | 'NEGADO'
  // Desfecho por cancelamento do cliente — faltava aqui e o tsc não acusava porque nada
  // switchava sobre o valor de forma exaustiva (ClickUp 86ak6vee4).
  | 'CANCELADO';

export type RitoProcessoArbitral = 'ARBITRIO' | 'SUMARIO' | 'SUMARISSIMO' | 'ORDINARIO';

export type TipoTribunalArbitral = 'UNICO' | 'COLEGIADO';

export type StatusReabertura = Exclude<ProcessoArbitralStatus, 'CONCLUIDO' | 'NEGADO'>;

export type TipoProcessoArbitral = 'DESPEJO' | 'COMUM';

export type AguardandoProcesso =
  | 'TRIAGEM'
  | 'PAGAMENTO'
  | 'AR'
  | 'CITACAO'
  | 'MANIFESTACAO'
  | 'NOMEACAO'
  | 'SENTENCA'
  | 'TRANSITO'
  | 'NADA';

export type AutorMovimentacao = 'PARTE' | 'ARBITRO' | 'SECRETARIA' | 'SISTEMA';

export type ConteudoDocumentoTriagem =
  | { tipo: 'carregando' }
  | { tipo: 'erro' }
  | { tipo: 'excede-teto' }
  | { tipo: 'sem-preview' }
  | { tipo: 'imagem'; objectUrl: string }
  | { tipo: 'docx'; html: string }
  | { tipo: 'planilha'; abas: string[]; htmlDaAba: (aba: string) => string };

export interface UltimaMovimentacaoInfo {
  data: string;
  autorTipo: AutorMovimentacao;
  autorNome: string | null;
}

export type ListTab = 'ALL' | 'PENDING_ACTION' | 'AWAITING_PAYMENT' | 'CONCLUDED';

export type SituacaoTriagemApi = 'A_TRIAR' | 'EM_TRIAGEM' | 'CORRECAO' | 'APROVADO' | 'NEGADO';

/**
 * Espelha o enum `OrigemProcessoArbitral` do core (quem criou o processo). `API_EXTERNA` é o
 * relay do Bubble; `API_PUBLICA` é o processo aberto pelo cliente-escritório na API externa
 * arbitral (DEC-23: valor próprio, porque um fica fora da triagem e o outro entra).
 */
export type OrigemProcessoApi = 'PLATAFORMA' | 'API_EXTERNA' | 'API_PUBLICA';

/**
 * As origens que as telas de TRIAGEM pedem ao servidor — fonte única do valor no web,
 * e o lugar canônico desta justificativa (os pontos de injeção só registram o que é
 * local a cada um). Espelha `ORIGEM_DA_TRIAGEM` do core (DEC-25, core#1864).
 *
 * 🔴 Regra POSITIVA, espelhando o core: a triagem pede o que é nosso, e não "o que não
 * é do Bubble" — com a negativa, origem futura que ninguém previu nasceria DENTRO da
 * fila, que é o default inseguro que esta frente veio corrigir. Importe esta constante
 * em vez de escrever as origens soltas.
 *
 * ⚠️ Esta lista tem de ser a mesma do core: o resumo que alimenta o card é filtrado fixo no
 * servidor, e a lista que o web pede aqui é a aba. Divergir não dá erro — o card conta um
 * processo que a aba não mostra (a janela aceita na D6-6 enquanto o web antigo está no ar).
 *
 * São **seis** pontos de injeção: fila do painel, busca da fila, alvo do deep-link por
 * id, alvo do deep-link por número, card proativo de elegíveis (`fetchElegiveisTriagem`)
 * e os filtros da lista de triagem. O modo de falha de esquecer um deles é silencioso —
 * a chamada continua 200, só que contando um universo diferente do card.
 *
 * ⚙️ Ativação e reversão deste filtro (a ordem importa, e reverter pela metade devolve
 * 12.623 processos à fila): `documentation/ops/triagem-filtro-origem.md`.
 */
export const ORIGEM_TRIAGEM: OrigemProcessoApi[] = ['PLATAFORMA', 'API_PUBLICA'];
// Congelada em statement à parte: o tipo segue mutável porque o `WritableDraft` do Immer recusa
// tupla readonly, mas a referência vai para o estado e o Immer a congela no primeiro fetch —
// sem isto, um `.sort()` sobre a fonte única funcionaria antes do fetch e lançaria depois.
Object.freeze(ORIGEM_TRIAGEM);

export interface DecisaoAtivaResumo {
  decisaoId: string;
  tipo: string;
  publicarApos: string;
}

/** Item do checklist que a Secretaria reprovou, com o título que o core resolve. */
export interface ItemChecklistReprovado {
  chave: string;
  titulo: string;
}

export interface ResumoTriagem {
  aTriar: number;
  emTriagem: number;
  todos: number;
}

export interface ProcessoArbitralListItem {
  id: string;
  numeroProcesso: string | null;
  statusArbitral: ProcessoArbitralStatus;
  rito: RitoProcessoArbitral;
  dataEntrada: string | null;
  dataAtualizacao: string;
  flagDespejo: boolean;
  valorCausa: number | null;
  partesPrincipais?: Array<{
    id: string;
    nome: string;
    papel: string;
    papelProcessual?: string | null;
  }>;
  partesPolo?: Array<{
    id: string;
    nome: string;
    documento: string | null;
    papelProcessual: string | null;
  }>;
  partesPoloTotal?: number;
  pendenciasAbertas?: number;
  extracaoSemaforo?: string | null;
  extracaoStatus?: StatusExtracaoTriagemApi | null;
  iaConfiante?: boolean | null;
  situacaoTriagem?: SituacaoTriagemApi | null;
  decisaoAtiva?: DecisaoAtivaResumo | null;
  /** O que o "Aprovar" em lote lista ao pedir a justificativa (D24) — o checklist GRAVADO. */
  itensReprovados?: ItemChecklistReprovado[];
  /**
   * A sugestão de despejo da análise, para pré-marcar a linha do "Aprovar" em lote
   * (core#2160). Ausente em core anterior ao campo: a linha fica para a Secretaria definir.
   */
  despejoSugerido?: SugestaoDespejoIa;
  /**
   * A escolha de despejo já publicada (ajustes ou aprovação, D-01 = A), pela mesma régua do
   * checklist: o lote pré-marca por ela antes da sugestão da IA (core#2209). Ausente em core
   * anterior ao campo; `null` = ninguém marcou.
   */
  despejoDaUltimaDecisao?: boolean | null;
  prazoVencimento?: string | null;
  diasParaVencer?: number | null;
  /**
   * Por que o decurso ainda não pode sair (C49): um prazo que segura a sentença segue correndo — no
   * despejo, a purga. Vem pronto do core, com a data do fim, e é o mesmo texto que o lote devolve.
   * Ausente em core anterior ao campo; `null` = nada segura o decurso.
   */
  motivoDecursoAguardandoPrazo?: string | null;
  aguardando?: AguardandoProcesso;
  arbitroNome?: string | null;
  tipoProcesso?: TipoProcessoArbitral;
  ultimaMovimentacao?: UltimaMovimentacaoInfo;
  enderecoCompletoValidado?: boolean;
  manifestacaoDaParteRecebida?: boolean;
  temAr?: boolean;
  /** Mesmos flags de `validacaoComum.ts` (backend) — marcadores que o `statusArbitral` não
   * conhece (encerramento é gravado antes da transição, e a desistência tem marcador próprio),
   * então a elegibilidade client-side de ações em lote precisa deles além do status. */
  temDesistenciaRegistrada?: boolean;
  temEncerramentoRegistrado?: boolean;
  /** A citação já saiu: a partir daqui encerrar depende de ordem do árbitro. */
  temCitacaoPraticada?: boolean;
}

export interface ListProcessosResponse {
  items: ProcessoArbitralListItem[];
  total: number;
  page: number;
  limit: number;
  /**
   * Cursor da página SEGUINTE (core#1668), ou `null` quando a cadeia acabou.
   *
   * Três valores, três significados — e colapsar dois deles em um reabre o defeito:
   * - `string` → há continuação; mande-a de volta em `cursor`.
   * - `null` → **fim**. É o ÚNICO terminador da travessia (ver `INV-01` no painel).
   * - `undefined` → a chave **não veio na resposta**: core sem suporte a cursor. É a
   *   feature-detection do REQ-07, e é ela que separa "acabou" de "o servidor descartou
   *   o param em silêncio" (o validador do core não é `.strict()`).
   */
  nextCursor?: string | null;
  /**
   * Quantas linhas do recorte ficaram para trás desta página — o "N" de "Caso N de M"
   * fora da primeira página. `0` na primeira. `undefined` quando o campo não veio
   * (core antigo), e aí o cabeçalho cai no fallback "M casos" (EDGE-21).
   *
   * ⚠️ **Nunca** entra em condição de parada: `anteriores + items.length` pode alcançar
   * `total` com página real pela frente, porque as três leituras do core rodam em
   * `ReadCommitted` (um snapshot por statement). Só renderiza.
   */
  anteriores?: number;
}

export interface ExportarProcessosResultado {
  items: ProcessoArbitralListItem[];
  truncado: boolean;
}

export interface ListProcessosFilters {
  search?: string;
  status?: ProcessoArbitralStatus[];
  rito?: RitoProcessoArbitral[];
  situacaoTriagem?: SituacaoTriagemApi[];
  requerente?: string;
  /**
   * Recorta a listagem em UM caso, pelo id. Existe porque o painel de triagem
   * pagina sob demanda (web#1063): o caso pedido por deep-link ou pela lista
   * pode não estar na página carregada, e o card depende de campos que só a
   * LISTA produz (`situacaoTriagem`, `decisaoAtiva`, `iaConfiante`) — o endpoint
   * de detalhe não os tem.
   *
   * ⚠️ Mande sem `status`: no core o fragmento por id compõe por AND com o resto
   * do where, então um `status` da fila junto esconderia justamente o caso já
   * triado que se quer abrir.
   */
  processoId?: string;
  /**
   * Recorta por quem criou o processo. Param ausente = todas as origens
   * (comportamento do serviço). A triagem manda sempre `ORIGEM_TRIAGEM`. A Tela 1
   * manda um de dois recortes exclusivos: padrão `ORIGEM_TRIAGEM`, ou só `API_EXTERNA`
   * pelo switch de Bubble — nunca o Bubble misturado com os demais.
   *
   * ⚠️ Ao contrário de `status`, este param DEVE acompanhar `processoId` nas
   * telas de triagem: um deep-link para processo do Bubble tem de cair em
   * `CASO_FORA_DA_JANELA` em vez de abrir o painel de decisão para um caso que a
   * regra de produto pôs fora do ciclo de triagem.
   */
  origem?: OrigemProcessoApi[];
  tab?: ListTab;
  comAlerta?: boolean;
  avancados?: Record<string, string>;
  /**
   * Fronteira da página seguinte, **opaca** — sempre um `nextCursor` devolvido pelo
   * servidor, nunca montada no cliente (core#1668).
   *
   * ⚠️ Não acompanha `page`: `page > 1` junto de `cursor` é 400 no core. E o cursor
   * carrega a direção em que nasceu — usá-lo com outro `sortOrder` também é 400. Quem
   * pagina por cursor repete `sortBy`/`sortOrder`/filtros idênticos em toda a cadeia.
   */
  cursor?: string;
  page?: number;
  limit?: number;
  sortBy?: 'numeroProcesso' | 'dataEntrada' | 'dataAtualizacao' | 'statusArbitral' | 'pendencia';
  sortOrder?: 'asc' | 'desc';
}

export interface ProcessosFacets {
  total: number;
  porStatus: Partial<Record<ProcessoArbitralStatus, number>>;
  porAcao: Partial<Record<AcaoLoteId, number>>;
}

export type AcaoLoteId =
  | 'triar-lote'
  | 'atribuir-arbitro'
  | 'enviar-ar'
  | 'disparar-citacao'
  | 'certidao-decurso'
  | 'escolher-certidao-lote'
  | 'concluir-manifestacao-lote'
  | 'movimentacao-lote'
  | 'certidao-transito'
  | 'finalizar-desistencia'
  | 'liberar-advogados';

export interface LoteValidacaoResponse {
  acaoId: AcaoLoteId;
  elegiveis: Array<{ processoId: string; numeroProcesso: string | null }>;
  inelegiveis: Array<{ processoId: string; numeroProcesso: string | null; motivo: string }>;
}

export interface LoteExecucaoResponse {
  loteId: string;
  status: 'FINALIZADO';
  processados: Array<{ processoId: string; resumo: string }>;
  falhas: Array<{ processoId: string; motivo: string }>;
  inelegiveis: Array<{ processoId: string; numeroProcesso: string | null; motivo: string }>;
}

export interface LoteValidacaoInput {
  acaoId: AcaoLoteId;
  processoIds: string[];
  payload: Record<string, unknown>;
}

export interface LoteRequestInput extends LoteValidacaoInput {
  clientRequestId: string;
}

/**
 * Falha de execução do lote já traduzida para a UI. `conflitoIdempotencia` marca o
 * 409 do backend: o `clientRequestId` já foi consumido e o lote **foi** criado, então
 * não existe retry — só levar o operador ao resultado (`loteId`).
 */
export interface LoteExecucaoErro {
  mensagem: string;
  conflitoIdempotencia: boolean;
  loteId: string | null;
}

export type LoteModoArbitro = 'sortear' | 'unico';

export type LoteTipoCertidao = 'decurso' | 'purga';

export type LoteDestinatario = DestinatarioPendencia | 'TODOS';

export interface LoteFormState {
  templateId?: string;
  modoArbitro: LoteModoArbitro;
  arbitroUsuarioId: string;
  tipoCertidao: LoteTipoCertidao;
  justificativa: string;
  descricaoConcluso: string;
  conteudoMovimentacao: string;
  modeloAndamentoId: string;
  criarPendencia: boolean;
  pendenciaDestinatario: LoteDestinatario | '';
  motivoEncerramento: LoteMotivoEncerramento | '';
  motivoEncerramentoTexto: string;
}

/**
 * Motivos aceitos pelo encerramento — subconjunto de `MotivoEncerramentoArbitral` do core, sem
 * `NULIDADE_CITACAO` (que o payload do evento de desistência não aceita).
 */
export type LoteMotivoEncerramento =
  | 'SENTENCA_ARBITRAL'
  | 'DESISTENCIA_REQUERENTE'
  | 'ACORDO_EXTERNO'
  | 'FALTA_PAGAMENTO'
  | 'DECURSO_PRAZO'
  | 'OUTROS';

export interface PainelGeralCounts {
  triagemPendente: number;
  aguardandoRegularizacao: number;
  aguardandoPagamento: number;
  aguardandoOficioTerceiro: number;
  emCitacao: number;
  manifestacaoRecebida: number;
  aguardandoNomeacao: number;
  arbitroNomeado: number;
  emInstrucao: number;
  aguardandoSentenca: number;
  sentencaPublicada: number;
  transitoPendente: number;
  encerrados: number;
  negados: number;
  manifestacoesPendentes: number;
  pendenciasAbertas: number;
  prazosVencidos: number;
  prazosHoje: number;
}

export interface TriagemDecisao {
  decisao: 'APROVAR' | 'NEGAR' | 'PEDIR_CORRECAO';
  motivoNegacao?: string;
  textoMotivoNegacao?: string;
  /**
   * Na APROVAR e na PEDIR_CORRECAO, obrigatório nas duas (core#2160; item 8, D-02): sem ele o
   * core recusa com 400. Ausente na NEGAR, cujo ramo do core é `.strict()`.
   */
  flagDespejo?: boolean;
  observacaoSecretaria?: string;
  /** Exigida pelo core na aprovação com item reprovado no checklist (D24). */
  justificativaItensReprovados?: string;
  pendencias?: Array<{
    tipoDocumento: string;
    motivoTexto: string;
    prazoDiasUteis: number;
  }>;
}

export interface TriagemDecisaoLote {
  decisao: 'APROVAR' | 'NEGAR' | 'PEDIR_CORRECAO';
  motivoNegacao?: string;
  textoMotivoNegacao?: string;
  /**
   * Na APROVAR e na PEDIR_CORRECAO: a escolha de despejo de CADA processo (aprovação no
   * core#2160, D-01 = c; ajustes no item 8, D-02). O core recusa o lote sem ele e recusa, um a
   * um, o processo que ficar de fora — não há valor do lote inteiro, porque um "Sim" ou "Não"
   * único afirmaria pela Secretaria o que ela não olhou.
   */
  flagDespejoPorProcesso?: Record<string, boolean>;
  prazoDiasUteis?: number;
  observacaoSecretaria?: string;
  /** Um texto para o lote; o core só o grava nos processos com item reprovado (D24). */
  justificativaItensReprovados?: string;
}

interface ConclusoCampos {
  descricaoConcluso: string;
  arbitroProcessoIdOverride?: string;
}

/**
 * O prazo do árbitro, do jeito que o core aceita: dias úteis (ele calcula pelo calendário da
 * câmara) OU data de calendário `AAAA-MM-DD`. Nunca os dois — o core recusa o par, e os
 * `never` fazem o compilador recusar antes.
 *
 * Ausente = o core aplica o default da origem (10 dias úteis no andamento; nas manifestações,
 * 5 no sumaríssimo e 10 nos demais ritos; o `TipoPrazo` da câmara na de solicitação).
 */
type ConclusoPrazo =
  | { prazoDiasUteis?: never; dataVencimento?: never }
  | { prazoDiasUteis: number; dataVencimento?: never }
  | { dataVencimento: string; prazoDiasUteis?: never };

/** Sem eixo: o core resolve as manifestações registradas na tx da escrita (caminho do lote). */
type ConclusoDeTodasAsManifestacoes = ConclusoCampos & {
  manifestacoesReferenciadasIds?: never;
  andamentoOrigemId?: never;
};

/** A tupla não-vazia exclui `[]`, que o core recusa como operação inválida. */
type ConclusoDeManifestacoesEscolhidas = ConclusoCampos & {
  manifestacoesReferenciadasIds: [string, ...string[]];
  andamentoOrigemId?: never;
};

type ConclusoDeAndamento = ConclusoCampos & {
  andamentoOrigemId: string;
  manifestacoesReferenciadasIds?: never;
};

/**
 * As três formas que o core aceita. Os `never` fazem o compilador recusar os dois eixos juntos
 * — antes a exclusividade vivia em comentário e só falhava no 400 (review do PR #925).
 */
export type ConclusoInput = (
  | ConclusoDeTodasAsManifestacoes
  | ConclusoDeManifestacoesEscolhidas
  | ConclusoDeAndamento
) &
  ConclusoPrazo;

export interface ConclusoResult {
  pendenciaArbitroId: string;
  arbitroProcessoId: string;
  outboxEventId: string;
  dataVencimento: string;
}

export type SorteioAlgoritmo = 'pesos' | 'pesos_com_carga' | 'random';

export interface SorteioBody {
  algoritmo?: SorteioAlgoritmo;
  excluirArbitroIds?: string[];
  arbitroEscolhidoOverride?: string;
}

export interface CandidatoSorteio {
  arbitroId: string;
  /** Nome do árbitro (C2/C3: a tela mostra o nome, nunca o id). Opcional para o core anterior. */
  nome?: string;
  peso: number;
  cargaAtiva: number;
  cargaMaxAtiva: number | null;
  elegivel: boolean;
}

export interface SorteioSugestao {
  candidatos: CandidatoSorteio[];
  pesoSnapshot: Record<string, number>;
  sugeridoId: string | null;
  sugeridoNome?: string | null;
  /** Vaga recusada sem substituto (C2/C3): a prévia é a do re-sorteio, sem quem já passou. */
  substituicao?: boolean;
}

export interface SorteioResult {
  arbitroEscolhidoId: string;
  arbitroProcessoId: string;
  arbitroEscolhidoNome?: string;
  /** O core desviou para o sorteio do substituto da vaga recusada (C2/C3). */
  substituicao?: boolean;
  candidatos: CandidatoSorteio[];
  pesoSnapshot: Record<string, number>;
}

/**
 * Motivos de bloqueio devolvidos pelo guard do back — a mesma lista que barra a publicação nos três
 * caminhos (automático, manual e em lote).
 */
export type MotivoBloqueioTransitoApi =
  | 'PROCESSO_ENCERRADO'
  | 'DESISTENCIA_REGISTRADA'
  | 'SEM_ENTREGA_DA_INTIMACAO'
  | 'CALENDARIO_AUSENTE'
  | 'PRAZO_NAO_VENCIDO'
  | 'ESCLARECIMENTO_PENDENTE';

export interface TransitoElegibilidade {
  pode: boolean;
  bloqueios: MotivoBloqueioTransitoApi[];
  /** Frase pronta com TODOS os bloqueios, para a tela mostrar o motivo (rn4). */
  motivo: string;
  /** Instante de preclusão do prazo de esclarecimento; `null` quando não deu para calcular. */
  vencimentoAt: string | null;
  /** Primeira entrega confirmada da intimação de sentença — o marco do prazo. */
  marcoEm: string | null;
}

/**
 * Estado da consulta de elegibilidade como união discriminada, e não como
 * `TransitoElegibilidade | null`.
 *
 * Com `null`, quatro situações colapsavam num valor só — nunca buscado, buscando, falhou, e
 * (por omissão) resposta antiga ainda em memória. Duas consequências reais na review do #971:
 * reabrir o diálogo mantinha a resposta da abertura anterior enquanto o refetch corria, e
 * `carregando` era `false`, então bastava marcar o "reafirmado" para o botão liberar **sobre
 * snapshot velho** (COR-02) — a mesma classe de bug que esta frente veio corrigir. E a falha da
 * consulta era indistinguível de "carregando": o diálogo ficava em "Verificando…" para sempre
 * (SEC-01).
 */
export type EstadoDaConsultaTransito =
  | { fase: 'carregando' }
  | { fase: 'ok'; elegibilidade: TransitoElegibilidade }
  | { fase: 'erro'; mensagem: string };

export interface PublicarTransitoBody {
  /**
   * Opcional: ausente, o back emite a certidão do modelo ativo da categoria. O front NÃO tem de
   * onde tirar um id de certidão — era exatamente esse buraco que fazia o botão enviar um id de
   * mock e a publicação falhar sempre.
   */
  certidaoId?: string;
  motivoEncerramento?:
    | 'SENTENCA_ARBITRAL'
    | 'DESISTENCIA_REQUERENTE'
    | 'ACORDO_EXTERNO'
    | 'FALTA_PAGAMENTO'
    | 'DECURSO_PRAZO'
    | 'OUTROS';
}

export interface PublicarTransitoResult {
  processoArbitralId: string;
  certidaoId: string;
  outboxEventId: string;
}

export interface PrazoItem {
  id: string;
  processoArbitralId: string;
  numeroProcesso: string | null;
  tipo: string;
  titulo: string;
  descricao?: string | null;
  dataVencimento: string;
  status: 'ABERTA' | 'RESOLVIDA' | 'REJEITADA' | 'ENCERRADA_PELA_SENTENCA';
  diasParaVencer: number;
  responsavelTipo?: 'PARTE' | 'ARBITRO' | 'SECRETARIA' | 'SISTEMA';
}

export type FiltroUrgenciaPrazo = 'todos' | 'vencidos' | 'hoje' | 'urgentes';

export type CanalManifestacao =
  | 'PLATAFORMA_CHAT'
  | 'PORTAL_WEB'
  | 'WHATSAPP'
  | 'EMAIL'
  | 'RECLAME_AQUI'
  | 'TELEFONE'
  | 'PRESENCIAL'
  | 'OUTRO';

export type StatusManifestacao = 'REGISTRADA' | 'DESCONSIDERADA' | 'PENDENTE';

export type RemetenteTipoManifestacao =
  | 'REQUERENTE'
  | 'REQUERIDO'
  | 'ADVOGADO_REQUERENTE'
  | 'ADVOGADO_REQUERIDO'
  | 'SECRETARIA'
  | 'ARBITRO'
  | 'SISTEMA';

export interface ManifestacaoRemetente {
  parteArbitralId: string | null;
  nome: string | null;
  papel: string | null;
}

export interface ManifestacaoAnexo {
  id: string;
  nome: string;
  mimeType: string | null;
  tamanhoBytes: number | null;
  sigilo: boolean;
}

export interface ManifestacaoListItem {
  id: string;
  processoArbitralId: string;
  status: StatusManifestacao;
  canal: CanalManifestacao;
  remetenteTipo: RemetenteTipoManifestacao;
  remetente: ManifestacaoRemetente;
  conteudo: string | null;
  anexos: ManifestacaoAnexo[];
  sigiloso: boolean;
  dataRecebimento: string;
  registradaEm: string | null;
  registradoPor: string | null;
  desconsideradaEm: string | null;
  justificativaDesconsiderada: string | null;
  andamentoId: string | null;
  /**
   * Concluso que LEVOU esta manifestação ao árbitro, quando houve. É o terminador da régua no
   * core: **nunca** volta a `null` — manifestação vai a um concluso uma vez só. Por isso o
   * rótulo na tela é "já foi", diferente do `conclusoAbertoId` do andamento, que é "está com".
   *
   * OPCIONAL de propósito, diferente do irmão do andamento: `listarManifestacoes` devolve o
   * payload por cast cru, sem parser de boundary, então declarar obrigatório seria mentir
   * enquanto o core não expuser o campo (review do PR #925, COR-05).
   */
  conclusoPendenciaId?: string | null;
}

export interface CriarManifestacaoInput {
  parteArbitralId: string;
  canal: CanalManifestacao;
  conteudo?: string;
  arquivos: File[];
  sigilo: boolean;
  submissionId: string;
  /**
   * Dia em que a manifestação chegou, chave `yyyy-mm-dd` (não instante). O service
   * converte para o instante de 00:00 em São Paulo ao montar o corpo — a UI só conhece
   * o dia, porque é isso que a Secretaria sabe.
   */
  diaRecebimento: string;
}

/** Parte do processo que pode figurar como remetente no registro manual. */
export interface ParteElegivelManifestacao {
  parteArbitralId: string;
  nome: string;
  papel: string;
}

export type DestinatarioPendencia = 'REQUERENTE' | 'REQUERIDO' | 'ARBITRO' | 'SECRETARIA';

export type StatusPendencia =
  | 'ABERTA'
  | 'EXPIRADA'
  | 'AGUARDANDO_VALIDACAO'
  | 'REJEITADA'
  | 'RESOLVIDA'
  | 'ENCERRADA_PELA_SENTENCA';

export interface PendenciaListItem {
  id: string;
  tipo: string;
  status: StatusPendencia;
  destinatario: DestinatarioPendencia | null;
  titulo: string;
  descricao: string | null;
  parteArbitralId: string | null;
  arbitroProcessoId: string | null;
  manifestacoesReferenciadas: unknown;
  dataVencimento: string | null;
  dataResposta: string | null;
  dataCriacao: string;
}

export type UrgenciaPrazo = 'SEM_PRAZO' | 'VENCIDA' | 'VENCE_HOJE' | 'VENCE_EM_BREVE' | 'NO_PRAZO';

export type OrigemRastroPendencia = 'PESSOA' | 'SISTEMA';

export interface RastroPendenciaEntry {
  tipo: 'STATUS' | 'ANEXO' | 'ANDAMENTO' | 'PENDENCIA_ORIGEM' | 'RESPOSTA_ORIGEM';
  origem: OrigemRastroPendencia;
  autor: string | null;
  statusAnterior: StatusPendencia | null;
  statusNovo: StatusPendencia | null;
  descricao: string | null;
  andamentoId: string | null;
  dataEvento: string;
}

export interface DestinatarioResolvidoPendencia {
  tipo: 'PARTE' | 'ARBITRO';
  nome: string;
}

export interface AnexoPendencia {
  id: string;
  nome: string;
  dataUpload: string;
}

export type SolicitantePendencia = 'SECRETARIA' | 'SISTEMA' | 'ARBITRO';

export interface PendenciaSecretariaItem extends PendenciaListItem {
  origem?: OrigemPedido | null;
  grupoCriacaoId: string | null;
  solicitante: SolicitantePendencia;
  requerValidacao: boolean;
  urgencia: UrgenciaPrazo;
  diasParaVencimento: number | null;
  destinatarioResolvido: DestinatarioResolvidoPendencia | null;
  anexos: AnexoPendencia[];
  rastro: RastroPendenciaEntry[];
}

export type FiltroPendencia = 'ativas' | 'finalizadas' | 'todas';

export interface ContagemPendencias {
  ativas: number;
  finalizadas: number;
  todas: number;
}

export interface ListarPendenciasSecretariaResult {
  items: PendenciaSecretariaItem[];
  total: number;
  contagem: ContagemPendencias;
}

export interface ListarPendenciasFilters {
  filtro?: FiltroPendencia;
  page?: number;
  limit?: number;
}

export type ValidacaoPendenciaAcao = 'aprovar' | 'rejeitar';

/** Prazo novo da recusa: dias úteis OU data — o core recusa os dois juntos. */
export type PrazoDaRecusa = { prazoDiasUteis: number } | { dataVencimento: string };

/** A pendência que a Secretaria está validando, com o que o diálogo mostra dela. */
export interface PendenciaEmValidacao {
  processoArbitralId: string;
  pendenciaId: string;
  titulo: string;
  respostaTexto: string | null;
}

export interface ValidarPendenciaResult {
  pendenciaId: string;
  statusAnterior: string;
  /** Recusa devolve a pendência à parte (ABERTA, com prazo novo) — ver core #2027. */
  novoStatus: 'RESOLVIDA' | 'ABERTA';
}

/** Sempre a Arbitralis (core C41): o `id` era do escritório que protocolou, não da câmara. */
export interface ProcessoDetalheCamara {
  nome: string;
}

export interface ProcessoDetalheContrato {
  id: string;
  produtoNome: string;
  versao: number;
}

export interface ProcessoDetalheParte {
  id: string;
  parteId: string;
  papel: string;
  papelProcessual?: string | null;
  /** Polo que o advogado/representante atende; null p/ parte direta ou sem lado registrado. */
  ladoRepresentado?: 'requerente' | 'requerido' | null;
  nome: string;
}

export interface ProcessoDetalheArbitro {
  id: string;
  usuarioId: string;
  nome: string;
  status: string;
  tipo: string;
  dataDesignacao: string;
  dataAceite: string | null;
}

export interface ProcessoDetalhe {
  id: string;
  numeroProcesso: string | null;
  statusArbitral: ProcessoArbitralStatus;
  rito: RitoProcessoArbitral;
  tipoTribunal: TipoTribunalArbitral;
  flagDespejo: boolean;
  clausulaArbitragem: boolean | null;
  valorCausa: string | null;
  breveRelato: string | null;
  assistenciaPedido: string | null;
  pedidoLiminar: boolean;
  descricaoUrgencia: string | null;
  billingModalitySnapshot: string | null;
  camara: ProcessoDetalheCamara;
  contrato: ProcessoDetalheContrato | null;
  dataAbertura: string;
  dataEntrada: string | null;
  dataAtualizacao: string;
  motivoNegacao: string | null;
  partes: ProcessoDetalheParte[];
  arbitros: ProcessoDetalheArbitro[];
  counts: {
    andamentos: number;
    documentos: number;
    certificados: number;
    pendenciasAbertas: number;
    /**
     * As ativas da D-03 (`ABERTA ∪ EXPIRADA ∪ AGUARDANDO_VALIDACAO ∪ REJEITADA`) — o mesmo
     * conjunto que a aba mostra por padrão. Opcional porque só existe a partir do core #1176;
     * enquanto ele não estiver em produção o consumidor cai em `pendenciasAbertas`.
     */
    pendenciasAtivas?: number;
    manifestacoesRegistradas: number;
    manifestacoesPendentes: number;
    solicitacoesPendentes: number;
  };
  aguardando: AguardandoProcesso;
  acoes: ProcessoDetalheAcao[];
  /**
   * Veredito da trava da sentença (G12) para o diálogo de liberação. Opcional porque só existe a
   * partir do arbitralis-core-api#2284: sem ele o diálogo não lista prazos, e o menu continua
   * governado por `acoes[]`.
   */
  travaSentenca?: TravaSentencaDetalhe;
  acoesDisponiveis: {
    iniciarTriagem: boolean;
    triagem: boolean;
    publicarDecurso: boolean;
    sortearArbitro: boolean;
    exigirTermoImparcialidade: boolean;
    publicarTermoArbitragem: boolean;
    fazerConcluso: boolean;
    intimarSentenca: boolean;
    publicarTransito: boolean;
  };
}

/**
 * Item do menu de ações do detalhe — elegibilidade e motivo derivados no core
 * do MESMO validarItem das ações em lote; `destaque` = a ação que o servidor recomenda agora
 * (vai à frente na faixa de ações).
 */
export interface ProcessoDetalheAcao {
  id: string;
  grupo: 'EXECUCAO' | 'EXPORTACAO';
  disponivel: boolean;
  motivo: string | null;
  destaque: boolean;
}

export interface AcoesDaFaixa {
  disponiveis: ProcessoDetalheAcao[];
  indisponiveis: ProcessoDetalheAcao[];
}

export interface PrazoTravaSentenca {
  prazoId: string;
  nome: string;
  /** ISO — instante com hora, não DATE. */
  vencimentoAt: string;
}

export interface LiberacaoSentencaVigente {
  overrideId: string;
  /** `null` só se a linha de override não trouxer o snapshot versionado. */
  motivo: MotivoLiberacaoSentenca | null;
  liberadaEm: string;
  responsavelId: string;
  prazosCobertos: PrazoTravaSentenca[];
}

export interface TravaSentencaDetalhe {
  /** Prazos que travam agora, em ordem de vencimento; vazio se nada trava ou já liberada. */
  prazos: PrazoTravaSentenca[];
  liberaEm: string | null;
  liberacao: LiberacaoSentencaVigente | null;
}

export interface LiberarSentencaBody extends VinculosEscritaInput {
  motivo: MotivoLiberacaoSentenca;
  justificativa: string;
}

export interface LiberarSentencaResult {
  liberacaoId: string;
  liberadaEm: string;
  motivo: MotivoLiberacaoSentenca;
  prazosCobertos: PrazoTravaSentenca[];
}

export interface ReabrirProcessoBody {
  novoStatus: StatusReabertura;
  motivo: string;
}

export interface ReabrirProcessoResult {
  processoArbitralId: string;
  statusAnterior: ProcessoArbitralStatus;
  novoStatus: ProcessoArbitralStatus;
}

export interface PublicarDecursoBody {
  templateId?: string;
  override?: { justificativa: string };
}

export interface PublicarDecursoResult {
  processoArbitralId: string;
  certificadoId: string;
  manifestacoesBloqueantes: string[];
}

export interface RankingArbitroItem {
  id?: string;
  arbitroId: string;
  peso: number;
  cargaMaxAtiva: number | null;
  observacao: string | null;
  ativo: boolean;
}

export type StatusCorreios =
  | 'PENDENTE'
  | 'POSTADO'
  | 'EM_TRANSITO'
  | 'SAIU_PARA_ENTREGA'
  | 'ENTREGUE'
  | 'RECUSADO'
  | 'ENDERECO_INVALIDO'
  | 'AUSENTE'
  | 'DEVOLVIDO'
  | 'CANCELADO'
  | 'EM_PRODUCAO';

export type OrigemAr = 'EXTRAJUDICIAL' | 'ARBITRAL';

export type StatusPrazoAr = 'NO_PRAZO' | 'VENCIDO' | 'RETORNADO' | 'SEM_POSTAGEM';

export type TipoDocumentoAr = 'ENVIADO' | 'COMPROVANTE_CARTEIRO' | 'AR';

export interface DocumentoAr {
  tipo: TipoDocumentoAr;
  nome: string;
  url: string | null;
}

/**
 * O que a linha da lista representa. Sem isto, o clique numa linha de solicitação
 * mandaria o `solicitacaoId` para `GET /ar/:envioFisicoId` e levaria 404 — a
 * solicitação não é um envio físico.
 */
export type TipoLinhaAr = 'ENVIO_FISICO' | 'SOLICITACAO_ENVIO_AR';

/** O que a coluna Status exibe: o ciclo postal **mais** "aguardando autorização". */
export type SituacaoAr = StatusCorreios | 'AGUARDANDO_AUTORIZACAO';

/**
 * Onde está a cobrança da carta — **não** é status de entrega, e não substitui
 * `situacao`: uma solicitação "Aguardando autorização" pode ter cobrança em aberto,
 * paga ou com emissão falha, e é só a falha que pede ação da Secretaria.
 *
 * Reexportado de `@/types/arbitral/envioArTypes`, não redeclarado: é o MESMO contrato
 * que o card do requerente consome, e a régua que o deriva mora num lugar só no
 * backend. Duas cópias divergiriam no dia em que o back ganhasse um estado novo — e
 * foi exatamente a divergência entre duas leituras da mesma regra que produziu o bug
 * da 2ª via neste PR.
 *
 * Importado no topo E reexportado aqui: `export type { X } from '…'` sozinho não põe `X`
 * no escopo do arquivo, e as interfaces abaixo que o usam não compilavam.
 */
export type { SituacaoCobrancaAr };

export interface ArListItem {
  id: string;
  origem: OrigemAr;
  tipoLinha: TipoLinhaAr;
  /** A VERDADE da linha — é isto que a coluna Status lê, não `statusCorreios`. */
  situacao: SituacaoAr;
  solicitacaoId: string | null;
  processoArbitralId: string | null;
  numeroProcesso: string | null;
  numeroNotificacao: string | null;
  notificacaoId: string | null;
  itemExtrajudicialId: string | null;
  trackingCode: string | null;
  /**
   * Linhas de solicitação levam `'PENDENTE'` (nada postado). Continua obrigatório —
   * contrato aditivo. Para exibir status, use `situacao`.
   */
  statusCorreios: StatusCorreios;
  statusPrazo: StatusPrazoAr;
  provider: string;
  parteNome: string | null;
  physicalPostedAt: string | null;
  physicalDeliveredAt: string | null;
  dataCriacao: string;
  dataAtualizacao: string;
  arPath: string | null;
  custoTotal: string | null;
  /**
   * Campo ADITIVO: só as linhas de solicitação o carregam, e servidor antigo não o
   * manda. Opcional de propósito — a ausência significa "não sei", e a linha não
   * pode oferecer reemissão de cobrança com base em desconhecimento.
   */
  situacaoCobranca?: SituacaoCobrancaAr | null;
}

export interface ArResumo {
  total: number;
  emCurso: number;
  vencidos: number;
  retornados: number;
  /** Contam em `total`/`emCurso`, nunca em `vencidos`/`retornados` — nada foi postado. */
  aguardandoAutorizacao: number;
}

export interface ArListFiltros {
  search?: string;
  origem?: OrigemAr;
  prazo?: StatusPrazoAr;
  /** Aceita o token novo: a coluna Status filtra por `SituacaoAr`, não só postal. */
  status?: SituacaoAr[];
  page?: number;
  limit?: number;
}

export interface ArListResult {
  items: ArListItem[];
  total: number;
  page: number;
  limit: number;
  resumo: ArResumo;
  porOrigem: { arbitral: number; extrajudicial: number };
}

export interface ArHistoricoStatusItem {
  id: string;
  statusAnterior: StatusCorreios | null;
  statusNovo: StatusCorreios;
  motivo: string | null;
  dataEvento: string;
}

export interface ArDetalhe extends ArListItem {
  pdfPath: string;
  payloadEnviado: null;
  statusLog: unknown;
  trackingRecords: unknown;
  scheduledAt: string | null;
  canceledAt: string | null;
  historico: ArHistoricoStatusItem[];
  documentos: DocumentoAr[];
}

export interface ArTimelineEvento {
  id: string;
  dataISO: string | null;
  titulo: string;
  descricao: string | null;
  local: string | null;
}

/**
 * Uma linha da fila "A enviar" — um REQUERIDO, não um processo. Dois requeridos do
 * mesmo processo são duas linhas, com o número repetido.
 *
 * Sem `enderecos`: as sugestões só chegam no detalhe (o drawer as busca ao abrir),
 * porque carregá-las por linha encheria a listagem de dado que a tabela não mostra.
 */
export interface RequeridoAEnviarItem {
  /**
   * Opcionais: o core passa a mandá-los com a régua da citação, e a fila não pode depender deles
   * enquanto o deploy do back não chega. `null` = o requerido ainda não tem o dado.
   */
  statusCitacao?: StatusCitacaoRequerido | null;
  /** Por que a citação digital virou física — o que trouxe o requerido para a fila. */
  motivoEncerramentoFisico?: MotivoEncerramentoFisicoCitacao | null;
  /** A situação que o selo mostra (D-04), a mesma da Visão geral; ausente no core anterior. */
  situacaoCitacao?: SituacaoDaCitacao | string | null;
  parteArbitralId: string;
  processoArbitralId: string;
  numeroProcesso: string | null;
  nomeRequerido: string;
  nomeRequerente: string | null;
  /** Dias úteis desde a última TENTATIVA digital — não desde uma visualização. */
  diasUteisSemVisualizacao: number;
  situacaoEndereco: SituacaoEnderecoAr;
  /** Preenchido quando o requerente devolveu, recusou ou deixou o prazo vencer. */
  motivoRetorno: MotivoRetornoAr | null;
  observacaoRequerente: string | null;
  /** 1 na primeira solicitação; cresce a cada bate-bola com o requerente. */
  rodada: number;
  /**
   * A Secretaria pode preparar nova solicitação a partir desta linha? `false` depois de
   * o requerente RECUSAR: a linha fica na fila para ser lida, sem ação, até ele desfazer
   * a recusa. A regra é do servidor (`podeAbrirNovaRodada`) — a tela só obedece.
   */
  podeSolicitar: boolean;
}

/** O que o drawer carrega ao abrir: a linha + os candidatos de endereço. */
export interface RequeridoAEnviarDetalhe extends RequeridoAEnviarItem {
  sugestoes: SugestaoEnderecoAr[];
}

export interface ArAEnviarFiltros {
  search?: string;
  page?: number;
  limit?: number;
}

/**
 * Linhas PLANAS e paginadas pelo servidor.
 *
 * Os `grupos` do mock sumiram: a tabela já os achatava para renderizar, e agrupar no
 * cliente impedia paginar — a fila inteira vinha no mount.
 */
export interface ArAEnviarResult {
  items: RequeridoAEnviarItem[];
  /** Total do FILTRO — pagina a tabela. */
  total: number;
  /**
   * Total ignorando a busca — é o que a **contagem da aba** exibe, como as outras
   * abas (que saem de `porOrigem` e também não reagem ao termo buscado).
   */
  totalGeral: number;
  page: number;
  limit: number;
}

export interface SolicitarEnvioArPayload {
  /** Estável entre retries do MESMO preparo: é o que torna o retry um replay. */
  clientRequestId: string;
  parteArbitralId: string;
  endereco: EnderecoAr;
  origemEndereco: OrigemEnderecoAr;
  modalidade: ModalidadeEntregaAr;
  /** O total que a tela mostrou. O servidor recota e recusa se divergir. */
  valorTotalExibido: string;
  prazoAutorizacao: PrazoAndamentoSelecao;
  /**
   * Snapshot de EXECUÇÃO da carta: o modelo que será impresso e o prazo impresso
   * nele. A postagem automática lê os dois (mais o `shipType` cotado) — sem o
   * modelo ela para em `SEM_MODELO` e a carta paga não sai.
   *
   * ⚠️ **Opcionais, e nunca `null`.** O schema do servidor é `.strict()` com
   * `.optional()`: mandar `modeloCartaId: null` é 400, não "sem modelo". Campo
   * ausente omite a chave.
   */
  modeloCartaId?: string;
  prazoCartaDiasUteis?: number;
  andamento: { conteudo: string; modeloAndamentoId: string | null };
}

/**
 * Desfecho da reemissão da cobrança pedida pela Secretaria.
 *
 * `desfecho` é a AÇÃO que o reconciliador executou (`EMITIR`, `NADA`, `SEM_LOCK`…),
 * não a situação resultante — por isso `situacaoCobranca` vem separado, e `null`
 * quando o servidor não a informa. Nesse caso quem diz a verdade é a listagem
 * recarregada, não uma dedução a partir da ação.
 */
export interface ReemitirCobrancaArResultado {
  solicitacaoId: string;
  desfecho: string;
  situacaoCobranca: SituacaoCobrancaAr | null;
}

export interface SolicitarEnvioArResultado {
  solicitacaoId: string;
  pendenciaIds: string[];
  andamentoId: string;
  prazoAutorizacaoAt: string;
  replay: boolean;
}

/**
 * Resultado da consulta de CEP, DISCRIMINADO em vez de lançar.
 *
 * Os dois motivos exigem mensagens diferentes na tela: `NAO_ENCONTRADO` é veredito
 * sobre o CEP, `INDISPONIVEL` é falha nossa — e esta nunca pode acusar o endereço
 * do requerido de inválido.
 */
export type ConsultaCepResultado =
  | { ok: true; endereco: { logradouro: string; bairro: string; cidade: string; uf: string } }
  | { ok: false; motivo: 'NAO_ENCONTRADO' | 'INDISPONIVEL' };

export type StatusBadgeVariantHint = 'neutral' | 'success' | 'warning' | 'error' | 'brand';

export interface SortearArbitroButtonProps {
  processoId: string;
  onSuccess?: () => void;
}

export interface PublicarTransitoDialogProps {
  processoId: string;
  /**
   * Vem de quem já carregou o processo — o diálogo não inventa mais o número a partir de mock.
   * Aceita `null` porque é assim que a origem o guarda, e o fallback mora num lugar só: dentro do
   * diálogo. Cada chamador aplicando o seu produzia duas grafias do mesmo microcopy.
   */
  numeroProcesso?: string | null;
  open: boolean;
  onOpenChange: (aberto: boolean) => void;
  onSuccess?: () => void;
}

/**
 * O botão é o diálogo mais o gatilho — derivado, e não copiado: com duas declarações, a nota
 * sobre onde mora o fallback do número já tinha divergido (ela ficou no botão depois de o
 * `?? 'sem número'` se mudar para o diálogo).
 */
export type PublicarTransitoButtonProps = Omit<
  PublicarTransitoDialogProps,
  'open' | 'onOpenChange'
>;

export interface PublicarDecursoButtonProps {
  processoId: string;
  onSuccess?: () => void;
  /** Presente enquanto o prazo da linha corre: o botão fica desabilitado e mostra o texto. */
  motivoDesabilitado?: string | null;
}

import type { ReactNode } from 'react';

export interface TabButtonProps<T extends string> {
  current: T;
  value: T;
  onClick: (v: T) => void;
  children: ReactNode;
}

export interface RawProcessosListResponse {
  data?: {
    items?: ProcessoArbitralListItem[];
    total?: number;
    page?: number;
    limit?: number;
    nextCursor?: string | null;
    anteriores?: number;
  };
  items?: ProcessoArbitralListItem[];
  total?: number;
  page?: number;
  limit?: number;
  nextCursor?: string | null;
  anteriores?: number;
}

export * from './alertasProcesso';
export * from './arModelosCarta';
export * from './auditoriaProcesso';
export * from './detalheExtras';
export * from './painelPrazos';
export * from './partesAcesso';
export * from './partesEscrita';
export * from './registroCitacao';
export * from './registroIntimacaoSentenca';
export * from './sortearSubstituto';
export * from './statusCitacao';
export * from './tentarDeNovo';

/** Ciclo de vida da extração de triagem no motor. `SUPERSEDIDA` nunca chega ao
 * front — a API só devolve a extração vigente —, mas está no union porque é o
 * enum do contrato. */
export type StatusExtracaoTriagemApi = 'SOLICITADA' | 'CONCLUIDA' | 'FALHA' | 'SUPERSEDIDA';

export type SugestaoChecklistTriagem = 'OK' | 'ALERTA' | 'FALHA';
export type AvaliacaoChecklistTriagem = 'PENDENTE' | 'APROVADO' | 'REPROVADO';

export interface ChecklistTriagemAvaliacaoInput {
  chave: string;
  avaliacao: AvaliacaoChecklistTriagem;
  observacao?: string | null;
}
export type CategoriaChecklistTriagem =
  | 'PARTES'
  | 'REPRESENTACAO'
  | 'CLAUSULA_ARBITRAL'
  | 'IMOVEL'
  | 'DOCUMENTOS';

export interface ChecklistTriagemEvidencia {
  campo: string;
  valor: string | null;
  fonteNomeArquivo: string | null;
  /**
   * `arbitral_documentos.id` do documento citado, resolvido pelo core na ingestão. Opcional
   * porque core anterior ao campo não o envia; ausente conta como `null` (só nome exato e único).
   */
  fonteDocumentoId?: string | null;
  pagina: string | null;
  citacao: string | null;
  statusValidacao: string;
}

export interface ChecklistTriagemItem {
  chave: string;
  categoria: CategoriaChecklistTriagem;
  titulo: string;
  critico: boolean;
  sugestaoIa: SugestaoChecklistTriagem;
  evidencias: ChecklistTriagemEvidencia[];
  avaliacao: AvaliacaoChecklistTriagem;
  observacao: string | null;
  avaliadoEm: string | null;
  analiseDesatualizada: boolean;
}

/**
 * O que a ANÁLISE sugeriu sobre despejo — eixo separado do `flagDespejo` do processo,
 * que é a DECISÃO humana e vive em outro endpoint (detalhe/listagem/decisão).
 *
 * `NAO_AVALIADO` ≠ `NAO`, e essa distinção é a razão de o campo existir: sem ela, o
 * default `flagDespejo=false` do banco aparecia na tela sob o rótulo "IA", como se a
 * análise tivesse afirmado alguma coisa sobre processo que ela nunca leu. O back devolve
 * `NAO_AVALIADO` para extração ausente, em voo, falha, evidência reprovada, "Não
 * Encontrado" e resposta fora do vocabulário.
 */
export type SugestaoDespejoIa = 'SIM' | 'NAO' | 'NAO_AVALIADO';

/**
 * POR QUE a análise não sugeriu nada sobre despejo (A-24) — vocabulário do core
 * (`extracaoTriagemTypes.ts`), em ordem de precedência: quando duas condições valem
 * juntas, o back manda a de cima.
 */
export type MotivoDespejoNaoAvaliado =
  | 'SEM_EXTRACAO'
  | 'EXTRACAO_EM_ANDAMENTO'
  | 'EXTRACAO_FALHOU'
  | 'CAMPO_AUSENTE'
  | 'EVIDENCIA_REPROVADA'
  | 'PETICAO_NAO_ENCONTRADA'
  | 'RESPOSTA_INVALIDA';

export interface DespejoIaChecklist {
  sugerido: SugestaoDespejoIa;
  /** Evidência do `caso_despejo`; `null` sempre que `sugerido` é `NAO_AVALIADO`. */
  evidencia: ChecklistTriagemEvidencia | null;
  /**
   * Só vem com `NAO_AVALIADO`. Opcional porque o core anterior à A-24 não o manda — a
   * tela cai no aviso genérico em vez de inventar um porquê.
   */
  motivoNaoAvaliado?: MotivoDespejoNaoAvaliado;
}

export interface ChecklistTriagem {
  extracao: {
    id: string;
    status: StatusExtracaoTriagemApi;
    semaforo: string | null;
    solicitadaEm: string;
    concluidaEm: string | null;
  } | null;
  iaConfiante: boolean;
  /**
   * Fica FORA de `itens` de propósito: não é item de checklist com APROVADO/REPROVADO,
   * é um pré-preenchimento que a Secretaria confirma ou corrige.
   */
  despejoIa: DespejoIaChecklist;
  itens: ChecklistTriagemItem[];
  /**
   * A escolha de despejo da última decisão PUBLICADA que julgou despejo (APROVADA ou
   * REGULARIZACAO — item 8, D-01 = A): é o que pré-preenche o seletor na 2ª triagem, depois dos
   * ajustes. `null` quando nenhuma decisão julgou. Opcional porque o core anterior ao item 8
   * não o manda — sem ele a tela cai no prefill da IA, como antes.
   */
  despejoDaUltimaDecisao?: boolean | null;
}

export type StatusDecisaoPendente = 'AGENDADA' | 'FALHA_AGENDAMENTO' | 'FALHA_PUBLICACAO';

export interface TriagemDecisaoAtiva {
  decisaoId: string;
  tipo: 'APROVADA' | 'REGULARIZACAO' | 'NEGADA';
  status: StatusDecisaoPendente;
  publicarApos: string;
  erro: string | null;
}

export interface TriagemDecisaoAgendada {
  decisaoId: string;
  processoArbitralId: string;
  status: 'AGENDADA' | 'PUBLICADA' | 'DESFEITA';
  publicarApos: string;
}

export interface DecisaoAgendadaNoLote {
  processoArbitralId: string;
  decisaoId: string;
  publicarApos: string;
}

export interface ResultadoLoteTriagem {
  processados: string[];
  agendadas: DecisaoAgendadaNoLote[];
  falhas: Array<{ processoArbitralId: string; motivo: string }>;
}

export type TipoAtorAuditoriaTriagem = 'PESSOA' | 'IA' | 'SISTEMA';

export interface AuditoriaTriagemAtor {
  tipo: TipoAtorAuditoriaTriagem;
  nome: string | null;
  papel: string | null;
  componente: string | null;
  emNomeDeNome: string | null;
}

export interface AuditoriaTriagemItem {
  id: string;
  operacao: string;
  /** Resolvido no core. Opcional porque o mapper é cast puro: core sem o campo não é 400, é undefined. */
  rotulo?: string;
  dataOperacao: string;
  ator: AuditoriaTriagemAtor;
  dadosAnteriores: Record<string, unknown> | null;
  dadosNovos: Record<string, unknown> | null;
  metadados: Record<string, unknown> | null;
  ipAddress: string | null;
  userAgent: string | null;
}

export interface AuditoriaTriagemPage {
  items: AuditoriaTriagemItem[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface AuditoriaTriagemEstadoProcesso {
  page: AuditoriaTriagemPage | null;
  loading: boolean;
  error: string | null;
}

export type OrigemAvaliacaoChecklist = 'MANUAL' | 'ACEITE_SUGESTOES';

export type MotivoNegacaoTriagem =
  | 'AUSENCIA_CLAUSULA_ARBITRAL'
  | 'REQUERIDO_MORADOR'
  | 'AUSENCIA_PROCURACAO'
  | 'AUSENCIA_CONTRATO'
  | 'PETICAO_FORA_FORMATO'
  | 'CLAUSULA_OUTRA_CAMARA'
  | 'FALTA_PAGAMENTO'
  | 'DESISTENCIA_REQUERENTE'
  | 'OUTROS';

export interface EventoAuditoriaTriagem {
  id: string;
  ator: string;
  cargo: string;
  acao: string;
  detalhe?: string;
  dataHora: string;
  ip: string;
  local: string;
  dispositivo: string;
}
import type { OrigemPedido } from './origemPedidoTypes';
